import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllDiscussions } from '../../services/student/forumService';
import type { IDiscussion } from '../../types/forum';
import Loader from '../../components/common/Loader';
import { showToast } from '../../utils/showToast';
import { MessageSquare, User } from 'lucide-react';
import { getPageContent } from '../../services/pageService';
import { usePageMeta } from '../../hooks/usePageMeta';

const Forum: React.FC = () => {
  const [discussions, setDiscussions] = useState<IDiscussion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageContent, setPageContent] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [discussionsData, contentData] = await Promise.all([
          getAllDiscussions(),
          getPageContent('forum').catch((err) => {
            console.error('Error fetching forum page content:', err);
            return null;
          }),
        ]);
        setDiscussions(discussionsData);
        setPageContent(contentData);
      } catch (err: any) {
        setError(err.message);
        showToast(err.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const title = pageContent?.title || 'Community Forum';
  const subtitle = pageContent?.subtitle || 'Join discussions, ask questions, and share your insights with the ShreeGuruCool community.';

  usePageMeta({
    title,
    description: subtitle,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-red-600 font-medium">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="py-10 px-4">
      <div className="max-w-7xl mx-auto text-center mb-10">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-lg text-gray-600">
          {subtitle}
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {discussions.length === 0 ? (
          <p className="text-center text-gray-500">No discussions yet. Be the first to start one!</p>
        ) : (
          discussions.map((discussion) => (
            <div key={discussion._id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
              <Link to={`/dashboard/student/forum?id=${discussion._id}`} className="block">
                <h2 className="text-xl font-semibold text-blue-700 hover:text-blue-800">{discussion.title}</h2>
              </Link>
              <div className="flex items-center text-sm text-gray-600 mt-2">
                <User className="w-4 h-4 mr-2" />
                <span>By {discussion.authorName}</span>
                <span className="mx-2">•</span>
                <span>Category: {discussion.category}</span>
                <span className="mx-2">•</span>
                <span>{new Date(discussion.createdAt).toLocaleDateString()}</span>
              </div>
              <p className="text-gray-700 mt-3 line-clamp-2">{discussion.content}</p>
              <div className="flex justify-end items-center text-sm text-gray-500 mt-4">
                <MessageSquare className="w-4 h-4 mr-1" />
                <span>{discussion.replies.length} Replies</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Forum;